/**
 * System settings catalog: `definedAt` + built-in `default`.
 * Types for names/values are inferred from this tree — do not duplicate them by hand.
 */
export const settingCatalog = {
  defaults_booking_confirmation: {
    definedAt: 'workspace',
    default: '' as string
  },
  defaults_brand_image: {
    definedAt: 'workspace',
    default: '/images/atellio_brand_logo_dark.png'
  },
  defaults_country_code: { definedAt: 'workspace', default: 'us' },
  defaults_currency: { definedAt: 'workspace', default: 'GBP' },
  defaults_locale: { definedAt: 'workspace', default: 'en' },
  defaults_timezone: { definedAt: 'workspace', default: 'Europe/London' },
  role_missing_days: { definedAt: 'workspace', default: 0 },
  tags_fixed: { definedAt: 'workspace', default: true },
  docusign_template_id: { definedAt: 'workspace', default: '' },

  defaults_callsheet_billing: { definedAt: 'team', default: '' },
  defaults_callsheet_social: { definedAt: 'team', default: '' },
  defaults_callsheet_hard_drive: { definedAt: 'team', default: '' },
  defaults_callsheet_hard_drive_specs: { definedAt: 'team', default: '' },
  defaults_callsheet_external_recipients: {
    definedAt: 'team',
    default: [] as string[]
  },
  public_brand_image: {
    definedAt: 'team',
    default: '/images/atellio_brand_logo_dark.png'
  },
  public_company_name: { definedAt: 'team', default: '' },
  public_team_name: { definedAt: 'team', default: '' },
  public_platform_colour: { definedAt: 'team', default: '#f1f1f1' },
  public_primary_colour: { definedAt: 'team', default: '#190026' },
  public_secondary_colour: { definedAt: 'team', default: '#d9e3f0' },
  shortlist_qrcode_cs_mapping: {
    definedAt: 'team',
    default: { mapping: {}, options: {} } as {
      mapping: Record<string, number>;
      options: Record<string, string>;
    }
  },
  custom_nike_arrival_board_logo: { definedAt: 'team', default: '' },
  custom_nike_arrival_board_duration: { definedAt: 'team', default: 0 },
  custom_nike_default_arrival_time: { definedAt: 'team', default: '' }
} as const;

export type SettingCatalog = typeof settingCatalog;
export type SettingName = keyof SettingCatalog;

/** Catalog defaults are examples; writable values are the widened primitive/object type. */
type WidenSettingDefault<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T;

export type SettingValue<K extends SettingName> = WidenSettingDefault<
  SettingCatalog[K]['default']
>;
export type SettingDefinedAt<K extends SettingName> =
  SettingCatalog[K]['definedAt'];

export type WorkspaceSettingName = {
  [K in SettingName]: SettingDefinedAt<K> extends 'workspace' ? K : never;
}[SettingName];

export type TeamSettingName = {
  [K in SettingName]: SettingDefinedAt<K> extends 'team' ? K : never;
}[SettingName];

export function isSettingName(name: string): name is SettingName {
  return Object.prototype.hasOwnProperty.call(settingCatalog, name);
}
